import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Bot, DollarSign, Eye, EyeOff, Loader2, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BrandCard, BrandCardContent } from "@/components/brand/brand-card";
import { BrandInput } from "@/components/brand/brand-input";
import { BrandSelect } from "@/components/brand/brand-select";
import { Header } from "@/components/Header";
import { useSession } from "@/hooks/auth-hooks";
import {
	coerceAgentCategories,
	getAgentCategoryLabel,
} from "@/lib/agent-categories";

export const Route = createFileRoute("/admin/agents")({
	component: AdminAgentsPage,
});

type AdminAgent = {
	id: string;
	name: string;
	slug: string;
	categories?: string[];
	description: string;
	estimatedMrr: number;
	isPublic: boolean;
	isStealth?: boolean;
	listingStatus?: string;
	founderName?: string;
	website?: string;
	createdAt: number;
};

// Stable color per category, derived from the slug so it works for the whole
// canonical vocabulary without a hand-maintained map that drifts when slugs
// change. No category resolves to the gray fallback unless it's missing.
const CATEGORY_PALETTE = [
	"bg-blue-500/15 text-blue-400 ring-blue-500/20",
	"bg-violet-500/15 text-violet-400 ring-violet-500/20",
	"bg-green-500/15 text-green-400 ring-green-500/20",
	"bg-cyan-500/15 text-cyan-400 ring-cyan-500/20",
	"bg-pink-500/15 text-pink-400 ring-pink-500/20",
	"bg-amber-500/15 text-amber-400 ring-amber-500/20",
	"bg-rose-500/15 text-rose-400 ring-rose-500/20",
	"bg-teal-500/15 text-teal-400 ring-teal-500/20",
	"bg-indigo-500/15 text-indigo-400 ring-indigo-500/20",
	"bg-fuchsia-500/15 text-fuchsia-400 ring-fuchsia-500/20",
];

const CATEGORY_FALLBACK_STYLE =
	"bg-stone-500/15 text-stone-400 ring-stone-500/20";

function categoryBadgeClass(slug: string | undefined): string {
	if (!slug) return CATEGORY_FALLBACK_STYLE;
	let hash = 0;
	for (let i = 0; i < slug.length; i++) {
		hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
	}
	return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

const STATUS_STYLES: Record<string, string> = {
	approved: "bg-green-500/15 text-green-400 ring-green-500/20",
	pending: "bg-violet-500/15 text-violet-400 ring-violet-500/20",
	rejected: "bg-red-500/15 text-red-400 ring-red-500/20",
};

function AdminAgentsPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("all");

	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({
				to: "/login",
				search: { redirect: "/admin/agents" },
				replace: true,
			});
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { data: agents = [], isLoading } = useQuery({
		queryKey: ["admin-agents"],
		queryFn: async () => {
			const res = await fetch("/api/admin/list-agents", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load agents");
			const data = await res.json();
			return (data.agents ?? []) as AdminAgent[];
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	const categories = useMemo(() => {
		const cats = new Set(
			agents.flatMap((a) => coerceAgentCategories(a.categories)),
		);
		return [
			{ value: "all", label: "All categories" },
			...Array.from(cats)
				.toSorted()
				.map((c) => ({ value: c, label: getAgentCategoryLabel(c) })),
		];
	}, [agents]);

	const filteredAgents = useMemo(() => {
		let items = agents.toSorted(
			(a, b) => (b.estimatedMrr ?? 0) - (a.estimatedMrr ?? 0),
		);

		if (categoryFilter !== "all") {
			items = items.filter((a) =>
				coerceAgentCategories(a.categories).includes(categoryFilter),
			);
		}

		const query = search.trim().toLowerCase();
		if (!query) return items;

		return items.filter((a) => {
			const haystack = [
				a.name,
				a.slug,
				a.founderName,
				...coerceAgentCategories(a.categories).map(getAgentCategoryLabel),
			]
				.join(" ")
				.toLowerCase();
			return haystack.includes(query);
		});
	}, [agents, search, categoryFilter]);

	if (isPending || isLoading || !isAdmin) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<Loader2 className="size-8 animate-spin text-foreground" />
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<Header />

			<main className="max-w-5xl mx-auto px-4 py-8">
				<div className="mb-8">
					<h1 className="text-2xl font-normal text-foreground tracking-[-0.04em]">
						Agents
					</h1>
					<p className="mt-1 text-base text-muted-foreground">
						{agents.length} agent{agents.length !== 1 ? "s" : ""} total
					</p>
				</div>

				<div className="flex flex-col sm:flex-row gap-3 mb-6">
					<BrandInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search agents..."
						icon={<Search className="size-4" />}
						className="sm:max-w-xs"
					/>
					<BrandSelect
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						options={categories}
						className="sm:w-48"
					/>
				</div>

				<div className="space-y-3">
					{filteredAgents.map((agent) => {
						const agentCategories = coerceAgentCategories(agent.categories);
						const catStyle = categoryBadgeClass(agentCategories[0]);

						return (
							<BrandCard key={agent.id}>
								<BrandCardContent className="p-5">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2.5 mb-2">
												<span className="font-normal text-foreground truncate">
													{agent.name}
												</span>
												<span className="text-sm text-muted-foreground font-mono">
													/{agent.slug}
												</span>
											</div>

											<div className="flex flex-wrap items-center gap-2 mb-3">
												{agentCategories.map((category) => (
													<span
														key={category}
														className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-normal ring-1 ring-inset ${catStyle}`}
													>
														{getAgentCategoryLabel(category)}
													</span>
												))}

												{agent.listingStatus && (
													<span
														className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-normal ring-1 ring-inset ${STATUS_STYLES[agent.listingStatus] ?? STATUS_STYLES.pending}`}
													>
														{agent.listingStatus}
													</span>
												)}

												{agent.isPublic ? (
													<span className="inline-flex items-center gap-1 text-sm text-green-400">
														<Eye className="size-3" />
														Public
													</span>
												) : (
													<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
														<EyeOff className="size-3" />
														Private
													</span>
												)}

												{agent.isStealth && (
													<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
														<EyeOff className="size-3" />
														Stealth
													</span>
												)}

												<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
													<DollarSign className="size-3" />$
													{agent.estimatedMrr?.toLocaleString() ?? "0"} MRR
												</span>
											</div>

											{agent.founderName && (
												<p className="text-sm text-muted-foreground">
													by {agent.founderName}
												</p>
											)}
										</div>
									</div>
								</BrandCardContent>
							</BrandCard>
						);
					})}

					{filteredAgents.length === 0 && (
						<BrandCard>
							<BrandCardContent className="py-16">
								<div className="text-center">
									<Bot className="size-10 text-muted-foreground/40 mx-auto mb-3" />
									<p className="text-base text-muted-foreground">
										{search || categoryFilter !== "all"
											? "No agents match your filters"
											: "No agents found"}
									</p>
								</div>
							</BrandCardContent>
						</BrandCard>
					)}
				</div>
			</main>
		</div>
	);
}
